import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddressDeclarationKind } from 'apps/webapp/src/app/@core/data/recruitment';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import ProvinceDataset from '../../../../../../data/hcm/dataset/provinces.json';
import RegionDataset from '../../../../../../data/hcm/dataset/regions.json';
import TownDataset from '../../../../../../data/hcm/dataset/towns.json';

@Component({
  selector: 'address-declaration',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    SelectModule,
    InputTextModule,
    DatePickerModule,
    TooltipModule,
  ],
  templateUrl: './address-declaration.component.html',
  styleUrls: ['./address-declaration.component.scss'],
  standalone: true,
})
export class AddressDeclarationComponent implements OnInit {
  @Input() form: FormGroup;

  translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  addressStateOptions = [
    { label: this.translate.instant(`selfservice-request.address-update.address-state.italy`), value: AddressDeclarationKind.ITALIAN },
    { label: this.translate.instant(`selfservice-request.address-update.address-state.other`), value: AddressDeclarationKind.FOREIGN },
  ];

  readonly regionOptions = RegionDataset;

  selectedStateOrRegion = signal<string | undefined>(undefined);
  selectedProvinceOrCountry = signal<string | undefined>(undefined);

  provinceOptions = computed(() => {
    const regionValue = this.selectedStateOrRegion();
    if (regionValue) {
      return ProvinceDataset.filter(province => province.region === regionValue);
    } else {
      return undefined;
    }
  });

  townOptions = computed(() => {
    const provinceCode = this.selectedProvinceOrCountry();
    if (provinceCode) {
      return TownDataset[provinceCode as keyof typeof TownDataset];
    } else {
      return undefined;
    }
  })

  constructor() { }

  ngOnInit(): void {
    this.syncAddressSelections();

    this.form.get('stateOrRegion')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncAddressSelections());

    this.form.get('provinceOrCounty')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncAddressSelections());
  }

  private syncAddressSelections(): void {
    this.selectedStateOrRegion.set(this.form.get('stateOrRegion')?.value ?? undefined);
    this.selectedProvinceOrCountry.set(this.form.get('provinceOrCounty')?.value ?? undefined);
  }

  onChangeKind(): void {
    this.form.get('stateOrRegion')?.setValue(undefined);
    this.form.get('provinceOrCounty')?.setValue(undefined);
    this.form.get('city')?.setValue(undefined);

    if (this.form.get('kind')?.value === 'ITALIAN') {
      this.form.get('stateOrRegion')?.setValidators([Validators.required]);
      this.form.get('provinceOrCounty')?.setValidators([Validators.required]);
      this.form.get('city')?.setValidators([Validators.required]);
    } else {
      this.form.get('stateOrRegion')?.clearValidators();
      this.form.get('provinceOrCounty')?.clearValidators();
      this.form.get('city')?.clearValidators();
    }

    this.form.get('stateOrRegion')?.updateValueAndValidity();
    this.form.get('provinceOrCounty')?.updateValueAndValidity();
    this.form.get('city')?.updateValueAndValidity();

    this.selectedStateOrRegion.set(undefined);
    this.selectedProvinceOrCountry.set(undefined);
  }

  onChangeStateOrRegion(event: string): void {
    this.selectedStateOrRegion.set(event);
    this.selectedProvinceOrCountry.set(undefined);
  }

  onChangeProvinceOrCountry(event: string): void {
    this.selectedProvinceOrCountry.set(event);
  }
}